import { ref as sRef, uploadBytes, UploadResult, StorageReference } from 'firebase/storage';

export interface ImageOptimizationOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    format?: 'image/webp' | 'image/jpeg' | 'image/png';
}

/**
 * Compresses an image file and converts it to WebP format using HTML5 Canvas.
 * Significantly reduces image size (usually 80-95% smaller) while maintaining visual fidelity.
 */
export async function compressImage(
    file: File,
    options: ImageOptimizationOptions = {}
): Promise<File> {
    // If not an image or is SVG / GIF (animations), don't touch canvas
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || file.type === 'image/gif') {
        return file;
    }

    const {
        maxWidth = 1920,
        maxHeight = 1920,
        quality = 0.85,
        format = 'image/webp'
    } = options;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;

            img.onload = () => {
                let width = img.width;
                let height = img.height;

                // Calculate scaled dimensions while preserving aspect ratio
                if (width > maxWidth || height > maxHeight) {
                    if (width / height > maxWidth / maxHeight) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    } else {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    // Fallback to original file if canvas context unavailable
                    resolve(file);
                    return;
                }

                // Smooth resizing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            resolve(file);
                            return;
                        }

                        // Determine new file name with .webp extension
                        const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                        const ext = format === 'image/webp' ? 'webp' : format === 'image/jpeg' ? 'jpg' : 'png';
                        const newFileName = `${baseName}.${ext}`;

                        const optimizedFile = new File([blob], newFileName, {
                            type: blob.type || format,
                            lastModified: Date.now(),
                        });

                        resolve(optimizedFile);
                    },
                    format,
                    quality
                );
            };

            img.onerror = (err) => {
                console.warn('Image load error during compression, using original:', err);
                resolve(file);
            };
        };

        reader.onerror = (err) => {
            console.warn('File read error during compression, using original:', err);
            resolve(file);
        };
    });
}

/**
 * Optimizes an image and uploads it to Firebase Storage with long-term cache headers.
 */
export async function uploadOptimizedImage(
    storageRef: StorageReference,
    file: File,
    options: ImageOptimizationOptions = {}
): Promise<UploadResult> {
    const optimizedFile = await compressImage(file, options);
    
    const metadata = {
        contentType: optimizedFile.type || 'image/webp',
        cacheControl: 'public, max-age=31536000, immutable', // 1 year browser cache
    };

    return await uploadBytes(storageRef, optimizedFile, metadata);
}
